import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { EyeIcon, PencilIcon, PlusIcon, TrashIcon } from '../../../components/ui';
import { accountingReturnState } from './navigation';

type ModuleRoutes = {
  detail: (id: number) => string;
  edit: (id: number) => string;
};

export const AccountingTableActions = <T extends { id: number }>({
  item,
  routes,
  onDelete,
  canDelete = true,
  canEdit = true,
  showEdit,
  showDelete,
}: {
  item: T;
  routes: ModuleRoutes;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
  showEdit?: (item: T) => boolean;
  showDelete?: (item: T) => boolean;
}): React.ReactElement => {
  const location = useLocation();
  const returnState = accountingReturnState(location.pathname, location.search);
  const editVisible = canEdit && (showEdit ? showEdit(item) : true);
  const deleteVisible = canDelete && (showDelete ? showDelete(item) : true);

  return (
    <div className="flex items-center flex-nowrap">
      <Link
        to={routes.detail(item.id)}
        state={returnState}
        className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="View"
        aria-label="View"
      >
        <EyeIcon className="w-5 h-5" />
      </Link>
      {editVisible && (
        <Link
          to={routes.edit(item.id)}
          state={returnState}
          className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="Edit"
          aria-label="Edit"
        >
          <PencilIcon className="w-5 h-5" />
        </Link>
      )}
      {deleteVisible && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id);
          }}
          className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete"
          aria-label="Delete"
        >
          <TrashIcon className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export const createGetTableActions = <T extends { id: number }>(
  routes: ModuleRoutes,
  options?: {
    showEdit?: (item: T) => boolean;
    showDelete?: (item: T) => boolean;
  },
) => (
  item: T,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  actionOptions?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <AccountingTableActions
    item={item}
    routes={routes}
    onDelete={onDelete}
    canDelete={actionOptions?.canDelete !== false}
    canEdit={actionOptions?.canEdit !== false}
    showEdit={options?.showEdit}
    showDelete={options?.showDelete}
  />
);

export const getAccountingToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return <PlusIcon className="w-5 h-5" />;
  }
  return null;
};
